"use client";

import { motion, useScroll, useTransform, useMotionValue, useAnimationFrame, type MotionValue } from "framer-motion";
import Image from "next/image";
import { useState, useMemo, useRef, useEffect } from "react";
import { TextLoop } from "@/components/ui/text-loop";

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

/* ------------------------------------------------------------------ */
/*  Sub-components for each card's visual content                     */
/* ------------------------------------------------------------------ */

function ScheduleMockup({ bookNow, timeSpecific, timeSpecificShort, locale, bookingHref = "/boka" }: { bookNow: string; timeSpecific: string; timeSpecificShort: string; locale: Locale; bookingHref?: string }) {
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedTime, setSelectedTime] = useState(0);
  const [offset] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  // Statically rendered pages bake "today" in at build time; recompute on the
  // client after mount so the strip shows the visitor's actual dates.
  const [today, setToday] = useState<Date | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Båda värdena finns bara hos besökaren: sidan är statiskt genererad, så
    // "idag" och fönsterbredden kan inte läsas under render utan att servern
    // och klienten renderar olika saker. Körs en gång, vid montering.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setToday(new Date());
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "start 0.3"],
  });

  const cardY = useTransform(scrollYProgress, [0, 0.8], [350, 0]);
  const cardOpacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);

  const dayCount = isMobile ? 3 : 6;

  const visibleDays = useMemo(() => {
    const base = today ?? new Date();
    return Array.from({ length: dayCount }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i + offset);
      return {
        index: i + offset,
        date: d.getDate(),
        weekday: d.toLocaleDateString(locale === "en" ? "en-US" : "sv-SE", { weekday: "short" }),
      };
    });
    // `locale` styr veckodagsnamnen och saknades i listan — på /en satt
    // svenska förkortningar kvar tills något annat råkade räkna om raden.
  }, [today, offset, dayCount, locale]);

  const timeSlots = isMobile
    ? ["ASAP", "08–12", "13–17", timeSpecificShort]
    : ["ASAP", "08:00–12:00", "13:00–17:00", timeSpecific];

  return (
    <div ref={cardRef} className="mt-5 flex flex-col items-center -mb-7 sm:-mb-8 -mx-7 sm:-mx-8 mt-auto overflow-hidden">
      <motion.div style={{ y: cardY, opacity: cardOpacity }} className="bg-white/90 backdrop-blur rounded-t-2xl p-4 pb-6 sm:p-5 sm:pb-7 flex flex-col gap-3 sm:gap-0 w-full max-w-[290px] sm:max-w-[420px] overflow-hidden">
        {/* Date picker */}
        <div className="flex items-center gap-2">
          {visibleDays.map((day) => (
            <button
              key={day.index}
              onClick={() => setSelectedDay(day.index)}
              className={`flex-1 h-10 sm:h-12 rounded-lg sm:rounded-xl flex flex-col items-center justify-center transition-all cursor-pointer ${
                selectedDay === day.index
                  ? "bg-brand-primary text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <span suppressHydrationWarning className={`text-[10px] font-medium leading-none mb-0.5 ${
                selectedDay === day.index ? "text-white/70" : "text-gray-400"
              }`}>
                {day.weekday}
              </span>
              <span suppressHydrationWarning className="text-[14px] font-bold">{day.date}</span>
            </button>
          ))}
        </div>

        {/* Time slots */}
        <div className="sm:block flex flex-col justify-center">
          <div className="flex gap-1.5 mt-1 sm:mt-4">
            {timeSlots.map((time, i) => (
              <button
                key={time}
                onClick={() => setSelectedTime(i)}
                className={`flex-1 py-2 px-1 rounded-lg text-[10px] font-semibold text-center transition-all cursor-pointer ${
                  selectedTime === i
                    ? "bg-brand-primary text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {time}
              </button>
            ))}
          </div>
          <a href={bookingHref} className="mt-4 sm:mt-9 mx-auto block rounded-xl bg-brand-primary px-8 py-2.5 text-sm font-bold text-white hover:brightness-110 transition-all cursor-pointer text-center">
            {bookNow}
          </a>
        </div>
      </motion.div>
    </div>
  );
}

function FoundersBounce() {
  const containerRef = useRef<HTMLDivElement>(null);
  const circleSize = 110;
  const r = circleSize / 2;
  const speed = 0.55;

  const founders = [
    { src: "/profiles/founder-1.png", alt: "Grundare 1" },
    { src: "/profiles/founder-2.png", alt: "Grundare 2" },
    { src: "/profiles/founder-3.png", alt: "Grundare 3" },
  ];

  /* Motion values for each circle — position, scale, opacity */
  const x0 = useMotionValue(0), y0 = useMotionValue(0), s0 = useMotionValue(0), o0 = useMotionValue(0);
  const x1 = useMotionValue(0), y1 = useMotionValue(0), s1 = useMotionValue(0), o1 = useMotionValue(0);
  const x2 = useMotionValue(0), y2 = useMotionValue(0), s2 = useMotionValue(0), o2 = useMotionValue(0);
  const mvX = [x0, x1, x2];
  const mvY = [y0, y1, y2];
  const mvS = [s0, s1, s2];
  const mvO = [o0, o1, o2];

  const state = useRef({
    init: false,
    inView: false,
    elapsed: 0,
    circles: [
      { x: 0, y: 0, vx: speed * 1.1, vy: speed * 0.7, delay: 0 },
      { x: 0, y: 0, vx: -speed * 0.85, vy: speed * 1.15, delay: 500 },
      { x: 0, y: 0, vx: speed * 0.7, vy: -speed * 1.05, delay: 1000 },
    ],
  });

  /* Intersection observer — only animate when visible */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { state.current.inView = e.isIntersecting; },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* Physics loop */
  useAnimationFrame((_, delta) => {
    const s = state.current;
    const el = containerRef.current;
    if (!el || !s.inView) return;

    const W = el.offsetWidth;
    const H = el.offsetHeight;

    /* Initialise positions once we know the size */
    if (!s.init) {
      s.circles[0].x = W * 0.15;
      s.circles[0].y = H * 0.1;
      s.circles[1].x = W * 0.55;
      s.circles[1].y = H * 0.08;
      s.circles[2].x = W * 0.3;
      s.circles[2].y = H * 0.55;
      s.init = true;
    }

    const dt = Math.min(delta, 50) / 16; // normalise to ~60 fps
    s.elapsed += delta;

    /* Update each circle */
    for (let i = 0; i < 3; i++) {
      const c = s.circles[i];
      const t = Math.min(1, Math.max(0, (s.elapsed - c.delay) / 500));

      /* easeOutBack pop-in */
      const k1 = 1.70158;
      const k3 = k1 + 1;
      const easedScale = t === 0 ? 0 : 1 + k3 * Math.pow(t - 1, 3) + k1 * Math.pow(t - 1, 2);

      mvO[i].set(t);
      mvS[i].set(Math.max(0, easedScale));

      if (t <= 0) continue;

      /* Move */
      c.x += c.vx * dt;
      c.y += c.vy * dt;

      /* Wall bounce */
      if (c.x <= 0) { c.x = 0; c.vx = Math.abs(c.vx); }
      if (c.x >= W - circleSize) { c.x = W - circleSize; c.vx = -Math.abs(c.vx); }
      if (c.y <= 0) { c.y = 0; c.vy = Math.abs(c.vy); }
      if (c.y >= H - circleSize) { c.y = H - circleSize; c.vy = -Math.abs(c.vy); }

      mvX[i].set(c.x);
      mvY[i].set(c.y);
    }

    /* Circle-circle elastic collision */
    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        const a = s.circles[i], b = s.circles[j];
        if (s.elapsed < a.delay || s.elapsed < b.delay) continue;

        const dx = (b.x + r) - (a.x + r);
        const dy = (b.y + r) - (a.y + r);
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < circleSize && dist > 0.01) {
          const nx = dx / dist, ny = dy / dist;
          const overlap = (circleSize - dist) / 2 + 0.5;
          a.x -= nx * overlap;
          a.y -= ny * overlap;
          b.x += nx * overlap;
          b.y += ny * overlap;

          const dvx = a.vx - b.vx, dvy = a.vy - b.vy;
          const dot = dvx * nx + dvy * ny;
          if (dot > 0) {
            a.vx -= dot * nx;
            a.vy -= dot * ny;
            b.vx += dot * nx;
            b.vy += dot * ny;
          }
        }
      }
    }
  });

  return (
    <div ref={containerRef} className="relative sm:w-[45%] min-h-[280px] sm:min-h-0 overflow-hidden">
      {founders.map((f, i) => (
        <motion.div
          key={i}
          className="absolute will-change-transform"
          style={{
            x: mvX[i],
            y: mvY[i],
            scale: mvS[i],
            opacity: mvO[i],
            width: circleSize,
            height: circleSize,
          }}
        >
          <Image
            src={f.src}
            alt={f.alt}
            width={110}
            height={110}
            sizes="110px"
            className="w-full h-full drop-shadow-[0_6px_20px_rgba(0,0,0,0.35)]"
          />
        </motion.div>
      ))}
    </div>
  );
}

function GuaranteeMockup({ insuredHeading, insured, parentProgress }: { insuredHeading: string; insured: string; parentProgress?: MotionValue<number> }) {
  const guaranteeRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress: ownProgress } = useScroll({
    target: guaranteeRef,
    offset: ["start end", "start 0.3"],
  });

  const scrollYProgress = parentProgress ?? ownProgress;
  const [b1s, b1e] = parentProgress ? [0.15, 0.33] : [0.5, 0.7];
  const [b2s, b2e] = parentProgress ? [0.28, 0.46] : [0.65, 0.85];

  const badge1Opacity = useTransform(scrollYProgress, [b1s, b1e], [0, 1]);
  const badge1Y = useTransform(scrollYProgress, [b1s, b1e], [20, 0]);
  const badge1Scale = useTransform(scrollYProgress, [b1s, b1e], [0.6, 1]);

  const badge2Opacity = useTransform(scrollYProgress, [b2s, b2e], [0, 1]);
  const badge2Y = useTransform(scrollYProgress, [b2s, b2e], [20, 0]);
  const badge2Scale = useTransform(scrollYProgress, [b2s, b2e], [0.6, 1]);

  const badges = [
    { opacity: badge1Opacity, y: badge1Y, scale: badge1Scale, style: { left: "38%", top: "8%" } },
    { opacity: badge2Opacity, y: badge2Y, scale: badge2Scale, style: { left: "76%", top: "2%" } },
  ];

  return (
    <div ref={guaranteeRef} className="flex flex-col gap-4">
      <h3 className="text-xl sm:text-2xl font-bold text-brand-dark leading-snug">
        {insuredHeading} <span className="underline decoration-brand-secondary decoration-2 underline-offset-4">{insured}</span>
      </h3>
      <div className="relative w-full h-[120px] sm:h-[140px]">
        <Image
          src="/insurance-wide.png"
          alt="Försäkrade möbler och prylar"
          fill
          className="object-contain object-center"
        />
        {badges.map((b, i) => (
          <motion.div
            key={i}
            className="absolute w-[28px] h-[28px] sm:w-[34px] sm:h-[34px] drop-shadow-md"
            style={{ ...b.style, opacity: b.opacity, y: b.y, scale: b.scale }}
          >
            <Image src="/trygg-badge.svg" alt="Trygg-Hansa verifierad" fill className="object-contain" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function RatingsMockup({ trustTitle, badgePro, badgeReliable, badgeFriendly, badgeEfficient, parentProgress }: { trustTitle: string; badgePro: string; badgeReliable: string; badgeFriendly: string; badgeEfficient: string; parentProgress?: MotionValue<number> }) {
  const ratingsRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress: ownProgress } = useScroll({
    target: ratingsRef,
    offset: ["start end", "start 0.3"],
  });

  const scrollYProgress = parentProgress ?? ownProgress;
  const base = parentProgress ? 0.52 : 0.1;
  const step = parentProgress ? 0.1 : 0.15;
  const dur = parentProgress ? 0.18 : 0.3;

  const badge1Opacity = useTransform(scrollYProgress, [base, base + dur], [0, 1]);
  const badge1Y = useTransform(scrollYProgress, [base, base + dur], [30, 0]);

  const badge2Opacity = useTransform(scrollYProgress, [base + step, base + step + dur], [0, 1]);
  const badge2Y = useTransform(scrollYProgress, [base + step, base + step + dur], [30, 0]);

  const badge3Opacity = useTransform(scrollYProgress, [base + step * 2, base + step * 2 + dur], [0, 1]);
  const badge3Y = useTransform(scrollYProgress, [base + step * 2, base + step * 2 + dur], [30, 0]);

  const badge4Opacity = useTransform(scrollYProgress, [base + step * 3, base + step * 3 + dur], [0, 1]);
  const badge4Y = useTransform(scrollYProgress, [base + step * 3, base + step * 3 + dur], [30, 0]);

  return (
    <div ref={ratingsRef} className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
      {/* Title + Gasell */}
      <div className="shrink-0 sm:w-[38%] flex flex-col gap-4">
        <h3 className="text-xl sm:text-2xl font-bold text-white leading-snug whitespace-pre-line">
          {trustTitle}
        </h3>
      </div>

      {/* Badge cluster */}
      <div className="relative sm:flex-1 min-h-[120px] sm:min-h-0 sm:h-[180px]">
        {/* Profile photos */}
        <div
          className="absolute left-1/2 -translate-x-1/2 z-10 flex -space-x-3 top-[18%] sm:top-[28%]"
        >
          <Image
            src="/team-viktor.png"
            alt="Viktor"
            width={66}
            height={66}
            className="w-[46px] h-[46px] sm:w-[66px] sm:h-[66px] object-contain z-[1]"
          />
          <Image
            src="/team-eugene.png"
            alt="Eugene"
            width={66}
            height={66}
            className="w-[46px] h-[46px] sm:w-[66px] sm:h-[66px] object-contain"
          />
        </div>

        {/* Name + stars */}
        <div
          className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10 top-[60%] sm:top-[65%]"
        >
          <p className="text-[10px] sm:text-xs font-semibold text-white whitespace-nowrap">
            Hanna & Lukas
          </p>
          <div className="flex gap-0.5 mt-0.5">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-brand-secondary"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
        </div>

        {/* Badge: Proffsiga */}
        <motion.div
          className="absolute px-2 py-1 sm:px-3 sm:py-1.5 text-center left-[15%] sm:left-[0%] top-[0%]"
          style={{ opacity: badge1Opacity, y: badge1Y }}
        >
          <p className="text-sm sm:text-lg">🤝</p>
          <p className="text-[8px] sm:text-[10px] text-white/70">{badgePro}</p>
        </motion.div>

        {/* Badge: Pålitliga */}
        <motion.div
          className="absolute px-2 py-1 sm:px-3 sm:py-1.5 text-center right-[15%] sm:right-[0%] top-[0%]"
          style={{ opacity: badge2Opacity, y: badge2Y }}
        >
          <p className="text-sm sm:text-lg">✅</p>
          <p className="text-[8px] sm:text-[10px] text-white/70">{badgeReliable}</p>
        </motion.div>

        {/* Badge: Trevliga */}
        <motion.div
          className="absolute px-2 py-1 sm:px-3 sm:py-1.5 text-center left-[15%] sm:left-[0%] top-[68%]"
          style={{ opacity: badge3Opacity, y: badge3Y }}
        >
          <p className="text-sm sm:text-lg">😊</p>
          <p className="text-[8px] sm:text-[10px] text-white/70">{badgeFriendly}</p>
        </motion.div>

        {/* Badge: Effektiva */}
        <motion.div
          className="absolute px-2 py-1 sm:px-3 sm:py-1.5 text-center right-[15%] sm:right-[0%] top-[68%]"
          style={{ opacity: badge4Opacity, y: badge4Y }}
        >
          <p className="text-sm sm:text-lg">💪</p>
          <p className="text-[8px] sm:text-[10px] text-white/70">{badgeEfficient}</p>
        </motion.div>
      </div>
    </div>
  );
}

function StatsMockup({ title, stats }: { title: string; stats: { emoji: string; label: string }[] }) {
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "start 0.3"],
  });

  return (
    <div ref={ref}>
      <h3 className="text-xl sm:text-2xl font-bold text-white leading-snug">
        {title}
      </h3>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {stats.map((stat, i) => (
          <StatRow
            key={i}
            stat={stat}
            index={i}
            total={stats.length}
            scrollYProgress={scrollYProgress}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * En rad i StatsMockup. Egen komponent för att animationen kräver två
 * useTransform per rad, och hookar får inte ligga i en .map() — antalet
 * hookanrop skulle följa antalet stats och ändras om listan någonsin ändrades.
 */
function StatRow({
  stat,
  index,
  total,
  scrollYProgress,
}: {
  stat: { emoji: string; label: string };
  index: number;
  total: number;
  scrollYProgress: MotionValue<number>;
}) {
  const start = 0.1 + index * 0.12;
  const end = start + 0.3;
  const opacity = useTransform(scrollYProgress, [start, end], [0, 1]);
  const y = useTransform(scrollYProgress, [start, end], [30, 0]);

  return (
    <motion.div
      className={`flex items-center gap-2 ${index === total - 1 && total % 2 !== 0 ? "col-span-2" : ""}`}
      style={{ opacity, y }}
    >
      <span className="text-lg sm:text-xl">{stat.emoji}</span>
      <span className="text-sm sm:text-base font-semibold text-white">{stat.label}</span>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

import type { Locale } from "./templates/ServicePageTemplate";

const featureT = {
  sv: {
    since: "Bortforsling sedan 2014",
    story: 'Vår "vi-löser-det-mentalitet" har sedan dess format allt vi gör och ungdomarna som jobbar hos oss. Idag hjälper vi privatpersoner och företag med snabb, prisvärd service utförd av pigga, drivna ungdomar.',
    loop1: "Din bokning gör skillnad",
    loop2: "Tillsammans får vi ut ungdomar i arbetslivet",
    bookYour: "Boka din bortforsling",
    bookDesc: "Ange din adress, fyll i information, och boka en tid som passar dig. Du ser priset och kan boka direkt.",
    bookNow: "Boka nu",
    vehicleTitle: "Anpassat fordon för din bortforsling",
    vehicleDesc: "Vi har flera fordon i olika storlekar. Vi väljer det fordon som passar ditt uppdrag bäst, så att bortforslingen blir så effektiv och kostnadseffektiv som möjligt.",
    insured: "försäkrade",
    insuredHeading: "Alltid",
    trustTitle: "Ungdomar att lita på!",
    badgePro: "Proffsiga",
    badgeReliable: "Pålitliga",
    badgeFriendly: "Trevliga",
    badgeEfficient: "Effektiva",
    timeSpecific: "Specifik tid",
    timeSpecificShort: "Specifik",
  },
  en: {
    since: "Junk removal since 2014",
    story: 'Our "we\'ll-figure-it-out mentality" has shaped everything we do and the young workers who join us. Today we help individuals and businesses with fast, affordable service carried out by energetic, driven young people.',
    loop1: "Your booking makes a difference",
    loop2: "Together, we create jobs for young people",
    bookYour: "Book your junk removal",
    bookDesc: "Enter your address, fill in the details, and pick a time that suits you. You'll see the price and can book instantly.",
    bookNow: "Book now",
    vehicleTitle: "The right vehicle for your junk removal",
    vehicleDesc: "We have multiple vehicles in different sizes. We pick the one that best fits your job, making the removal as efficient and cost-effective as possible.",
    insured: "insured",
    insuredHeading: "Always",
    trustTitle: "Young energy.\nReliable service.",
    badgePro: "Professional",
    badgeReliable: "Reliable",
    badgeFriendly: "Friendly",
    badgeEfficient: "Efficient",
    timeSpecific: "Specific time",
    timeSpecificShort: "Specific",
  },
};

interface FeatureCardsProps {
  heading?: string;
  sinceText?: string;
  storyText?: string;
  bookYourText?: string;
  bookDescText?: string;
  bookImage?: string;
  showScheduleMockup?: boolean;
  vehicleTitleText?: string;
  vehicleDescText?: string;
  showVehicleImages?: boolean;
  showGuarantee?: boolean;
  trustTitleText?: string;
  customStats?: { emoji: string; label: string }[];
  locale?: Locale;
  bookingHref?: string;
}

export default function FeatureCards({
  heading = "Vi sköter din bortforsling",
  sinceText,
  storyText,
  bookYourText,
  bookDescText,
  bookImage,
  showScheduleMockup = true,
  vehicleTitleText,
  vehicleDescText,
  showVehicleImages = true,
  showGuarantee = true,
  trustTitleText,
  customStats,
  locale = "sv",
  bookingHref = "/boka",
}: FeatureCardsProps = {}) {
  const overrides: Record<string, string> = {};
  if (sinceText) overrides.since = sinceText;
  if (storyText) overrides.story = storyText;
  if (bookYourText) overrides.bookYour = bookYourText;
  if (bookDescText) overrides.bookDesc = bookDescText;
  if (vehicleTitleText) overrides.vehicleTitle = vehicleTitleText;
  if (vehicleDescText) overrides.vehicleDesc = vehicleDescText;
  if (trustTitleText) overrides.trustTitle = trustTitleText;
  const l = { ...featureT[locale], ...overrides };
  const rightColRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: rightColProgress } = useScroll({
    target: rightColRef,
    offset: ["start end", "start 0.15"],
  });
  return (
    <section className="py-8 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark">
            {heading}
          </h2>
        </motion.div>

        {/* Bento grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-12 auto-rows-auto gap-5"
        >
          {/* 1 — Our story (top-left, brand primary) */}
          <motion.div
            variants={cardVariants}
            className="md:col-span-7 rounded-3xl overflow-hidden min-h-[370px] flex flex-col sm:flex-row"
            style={{ background: "linear-gradient(135deg, #2a4a63 0%, #1d3547 100%)" }}
          >
            {/* Text left */}
            <div className="p-7 sm:p-8 flex flex-col justify-between flex-1 sm:w-[55%]">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  {l.since}
                </h3>
                <div className="mt-5 space-y-4">
                  {l.story.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-white/90 text-base leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
              <div className="mt-6 text-sm sm:text-lg font-bold text-white break-words">
                <TextLoop interval={3} transition={{ duration: 0.4 }}>
                  <span className="block">{l.loop1}</span>
                  <span className="block">{l.loop2}</span>
                </TextLoop>
              </div>
            </div>
            {/* Founders — bouncing screensaver */}
            <FoundersBounce />
          </motion.div>

          {/* 2 — Schedule (top-right, brand secondary/orange) */}
          <motion.div
            variants={cardVariants}
            className={`md:col-span-5 rounded-3xl p-7 sm:p-8 flex flex-col overflow-hidden ${showScheduleMockup ? "min-h-[370px]" : ""}`}
            style={{ background: "linear-gradient(135deg, #f89544 0%, #e07a2a 100%)" }}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-white">
              {l.bookYour}
            </h3>
            <p className="mt-5 mb-4 text-white/90 text-base leading-relaxed">
              {l.bookDesc}
            </p>
            {showScheduleMockup && (
              <ScheduleMockup bookNow={l.bookNow} timeSpecific={l.timeSpecific} timeSpecificShort={l.timeSpecificShort} locale={locale} bookingHref={bookingHref} />
            )}
            {bookImage && (
              <div className="-mt-2 -mb-16 sm:-mb-20 -mx-2 flex justify-center">
                <Image
                  src={bookImage}
                  alt={l.bookYour}
                  width={500}
                  height={500}
                  className="w-[65%] h-auto object-contain"
                />
              </div>
            )}
          </motion.div>

          {/* Bottom row — vehicles left, guarantee + ratings stacked right */}
          <motion.div
            variants={cardVariants}
            className="md:col-span-12 flex flex-col md:flex-row gap-5"
          >
            {/* 3 — Vehicles (left) */}
            <div className="md:w-[58.33%] rounded-3xl bg-[#2a4a63]/10 p-7 sm:p-8 flex flex-col overflow-hidden">
              <h3 className="text-xl sm:text-2xl font-bold text-brand-dark">
                {l.vehicleTitle}
              </h3>
              <div className="mt-5 space-y-2 max-w-xl">
                {l.vehicleDesc.split('\n').map((line, i) => (
                  <p key={i} className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {line}
                  </p>
                ))}
              </div>
              {showVehicleImages && (
                <div className="mt-auto grid grid-cols-2 gap-4 pt-3 -mb-4 sm:-mb-24">
                  <div className="flex items-end justify-center">
                    <Image
                      src="/vehicle-van.png"
                      alt="Ungdomskraft skåpbil"
                      width={500}
                      height={500}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                  <div className="flex items-end justify-center">
                    <Image
                      src="/vehicle-truck.png"
                      alt="Ungdomskraft lastbil med bakgavellyft"
                      width={500}
                      height={500}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Right column — guarantee + ratings */}
            <div ref={rightColRef} className="md:w-[41.67%] flex flex-col gap-5">
              {/* 4 — Guarantee */}
              {showGuarantee && (
                <div className="rounded-3xl bg-gray-50 p-5 sm:p-6 overflow-hidden">
                  <GuaranteeMockup insuredHeading={l.insuredHeading} insured={l.insured} parentProgress={rightColProgress} />
                </div>
              )}

              {/* 5 — Ratings / Stats */}
              <div className="rounded-3xl bg-brand-primary p-5 sm:p-8 flex-1 flex flex-col overflow-hidden">
                {customStats ? (
                  <StatsMockup title={l.trustTitle} stats={customStats} />
                ) : (
                  <RatingsMockup trustTitle={l.trustTitle} badgePro={l.badgePro} badgeReliable={l.badgeReliable} badgeFriendly={l.badgeFriendly} badgeEfficient={l.badgeEfficient} parentProgress={showGuarantee ? rightColProgress : undefined} />
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
